import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { ArrowRight } from 'lucide-react';
import { previewInvite, registerWithInvite } from '../services/auth';
import {
  PublicCardHeader,
  PublicPageFrame,
  publicBodyFontFamily,
  publicInlineLinkSx,
  publicPalette,
  publicPrimaryButtonSx,
  publicSecondaryButtonSx,
  publicTextFieldSx,
} from '../components/common/PublicPageFrame';

const checkboxSx = {
  color: publicPalette.heroBlueMid,
  '&.Mui-checked': {
    color: publicPalette.heroBlueMid,
  },
};

function InviteRegistrationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tokenFromUrl = searchParams.get('token') || '';
  const [retryToken, setRetryToken] = useState(tokenFromUrl);
  const [inviteDetails, setInviteDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewError, setPreviewError] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    agreeTerms: false,
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    setRetryToken(tokenFromUrl);
  }, [tokenFromUrl]);

  const emailLocked = useMemo(() => Boolean(inviteDetails?.email), [inviteDetails]);
  const universityName = inviteDetails?.university?.name || inviteDetails?.universityName || 'your institution';

  useEffect(() => {
    let isActive = true;

    const fetchInvite = async () => {
      if (!tokenFromUrl) {
        if (isActive) {
          setPreviewError('No invite token was provided. Please use the link from your invitation email.');
          setInviteDetails(null);
          setLoading(false);
        }
        return;
      }

      if (isActive) {
        setLoading(true);
        setPreviewError('');
        setInviteDetails(null);
      }

      try {
        const details = await previewInvite(tokenFromUrl);
        if (!isActive) {
          return;
        }

        setInviteDetails(details);
        setFormData((prev) => ({
          ...prev,
          email: details.email || prev.email,
        }));
      } catch (requestError) {
        if (!isActive) {
          return;
        }

        const message = requestError.response?.data?.message
          || 'We could not validate that invite. Double-check the link or request a new one.';
        setPreviewError(message);
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    fetchInvite();

    return () => {
      isActive = false;
    };
  }, [tokenFromUrl]);

  const validateEmail = (email) => /\S+@\S+\.\S+/.test(email || '');
  const validatePassword = (password) => password && password.length >= 8;

  const validateForm = () => {
    const errors = {};

    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }

    if (!validateEmail(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!validatePassword(formData.password)) {
      errors.password = 'Password must be at least 8 characters';
    }

    if (formData.confirmPassword !== formData.password) {
      errors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.agreeTerms) {
      errors.agreeTerms = 'You must accept the terms to continue';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError('');
    setSuccessMessage('');

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await registerWithInvite({
        inviteToken: tokenFromUrl,
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
      });
      setSuccessMessage(response.message || 'Registration successful. You can now log in.');
    } catch (requestError) {
      const message = requestError.response?.data?.message
        || 'We were unable to complete your registration. Please contact your administrator.';
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRetry = () => {
    if (!retryToken.trim()) {
      return;
    }

    navigate(`/register/invite?token=${encodeURIComponent(retryToken.trim())}`);
  };

  return (
    <PublicPageFrame>
      <PublicCardHeader
        eyebrow="Invite registration"
        title="Accept your invite"
        description="Complete your profile and set a password to activate your Virtual Medic account."
      />

      {loading ? (
        <Stack spacing={2} alignItems="center" sx={{ py: 4 }}>
          <CircularProgress />
          <Typography sx={{ color: publicPalette.inkSoft, fontFamily: publicBodyFontFamily }}>
            Checking your invite...
          </Typography>
        </Stack>
      ) : previewError ? (
        <Stack spacing={3}>
          <Alert severity="error">{previewError}</Alert>
          <Typography sx={{ color: publicPalette.inkSoft, fontFamily: publicBodyFontFamily, lineHeight: 1.72 }}>
            Ask your programme administrator to resend the invite, or try a different token below.
          </Typography>
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Invite token"
              value={retryToken}
              onChange={(event) => setRetryToken(event.target.value)}
              placeholder="Paste a new invite token"
              sx={publicTextFieldSx}
            />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              <Button variant="contained" onClick={handleRetry} disabled={!retryToken.trim()} sx={publicPrimaryButtonSx}>
                Try this invite
              </Button>
              <Button component={Link} to="/signup" variant="outlined" sx={publicSecondaryButtonSx}>
                Back to sign up
              </Button>
            </Box>
          </Stack>
        </Stack>
      ) : (
        <Stack spacing={3}>
          <Box
            sx={{
              borderRadius: '22px',
              p: 2.5,
              bgcolor: alpha(publicPalette.heroBlue, 0.06),
              border: `1px solid ${alpha(publicPalette.heroBlueMid, 0.12)}`,
            }}
          >
            <Stack spacing={0.6}>
              <Typography sx={{ fontWeight: 800, color: publicPalette.ink, fontFamily: publicBodyFontFamily }}>
                Invitation details
              </Typography>
              <Typography sx={{ color: publicPalette.inkSoft, fontFamily: publicBodyFontFamily }}>
                {`Provisioned by ${universityName}.`}
              </Typography>
              {inviteDetails?.email && (
                <Typography sx={{ color: publicPalette.inkSoft, fontFamily: publicBodyFontFamily }}>
                  {`Invited email: ${inviteDetails.email}`}
                </Typography>
              )}
            </Stack>
          </Box>

          {submitError && <Alert severity="error">{submitError}</Alert>}
          {successMessage && (
            <Alert severity="success">
              {successMessage} <Link to="/login">Log in now</Link>.
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  id="invite-firstName"
                  label="First name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  error={Boolean(formErrors.firstName)}
                  helperText={formErrors.firstName}
                  sx={publicTextFieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  id="invite-lastName"
                  label="Last name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  error={Boolean(formErrors.lastName)}
                  helperText={formErrors.lastName}
                  sx={publicTextFieldSx}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  id="invite-email"
                  label="Email address"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={Boolean(formErrors.email)}
                  helperText={formErrors.email || (emailLocked ? 'This email is set by your administrator.' : 'Use the same email address that received the invite.')}
                  sx={publicTextFieldSx}
                  InputProps={{
                    readOnly: emailLocked,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  id="invite-password"
                  value={formData.password}
                  onChange={handleChange}
                  error={Boolean(formErrors.password)}
                  helperText={formErrors.password || 'Use at least 8 characters.'}
                  sx={publicTextFieldSx}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  name="confirmPassword"
                  label="Confirm password"
                  type={showPassword ? 'text' : 'password'}
                  id="invite-confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  error={Boolean(formErrors.confirmPassword)}
                  helperText={formErrors.confirmPassword}
                  sx={publicTextFieldSx}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  sx={{ alignItems: 'flex-start', m: 0 }}
                  control={(
                    <Checkbox
                      name="agreeTerms"
                      checked={formData.agreeTerms}
                      onChange={handleChange}
                      sx={checkboxSx}
                    />
                  )}
                  label={(
                    <Typography sx={{ color: publicPalette.inkSoft, fontFamily: publicBodyFontFamily, lineHeight: 1.6 }}>
                      I agree to the{' '}
                      <a href="/terms" target="_blank" rel="noreferrer">Terms of Service</a>
                      {' '}and{' '}
                      <a href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>.
                    </Typography>
                  )}
                />
                {formErrors.agreeTerms && (
                  <Typography variant="caption" color="error">
                    {formErrors.agreeTerms}
                  </Typography>
                )}
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 3 }}>
              <Button type="submit" variant="contained" disabled={isSubmitting || Boolean(successMessage)} sx={publicPrimaryButtonSx}>
                {isSubmitting ? 'Completing registration...' : 'Complete registration'}
              </Button>
              <Button component={Link} to="/signup" variant="outlined" sx={publicSecondaryButtonSx}>
                Back to sign up
              </Button>
            </Box>
          </Box>
        </Stack>
      )}

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        justifyContent="space-between"
        alignItems={{ sm: 'center' }}
        sx={{ mt: 3 }}
      >
        <Typography sx={{ color: publicPalette.inkSoft, fontFamily: publicBodyFontFamily }}>
          Need to start over?
        </Typography>
        <Button component={Link} to="/signup" endIcon={<ArrowRight size={16} />} sx={{ ...publicInlineLinkSx, px: 0 }}>
          Return to sign-up options
        </Button>
      </Stack>
    </PublicPageFrame>
  );
}

export default InviteRegistrationPage;
